import { Check, ImageIcon, Loader2, Upload } from 'lucide-react'
import { apiCall } from 'modules/core/services/api'
import { CreativeService } from 'openapi/services/CreativeService'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useImageUpload } from '../../../common/hooks/use-file-upload'
import type { PickerImageSelection } from '../../components/ImagePickerModal'

interface GeneratedImage {
  attachmentId: string
  imageUrl: string
  createdAt: string
  mimeType?: string
  ratio?: number
}

interface SelectedImage {
  attachmentId: string
  imageUrl: string
  mimeType?: string
  ratio?: number
}

interface ImagePickerBodyProps {
  creativeId: string
  onSelect: (image: PickerImageSelection) => void
  // Single-image mode: the parent's current image is synthesized into the grid
  // if not already present, and gets the acid check-mark (not locked — clicking
  // it is a harmless no-op).
  currentImageUrl?: string | null
  currentAttachmentId?: string | null
  // Append mode (carousel): every image already in the parent is synthesized
  // into the grid (if not already in the generated batch) as a check-marked +
  // locked tile, so the user sees the full state in one place. `onSelect` fires
  // once per click on an unlocked image and the parent appends.
  selectedImages?: SelectedImage[]
  // When the parent has hit a cap (e.g. carousel full at 10/10), disable the
  // upload button and the grid. `disabledHint` replaces the upload button.
  disabled?: boolean
  disabledHint?: string
  // Restrict upload mime. Carousels need `image/jpeg`; default allows any image.
  acceptMime?: string
}

/**
 * The inline picker — upload button + grid of created images. Extracted from
 * ImagePickerModal so it can be rendered directly inside the Edit modal
 * (no nested dialog). Drives both flows:
 *   - single (default): click replaces the parent's one image.
 *   - append (carousel): pass `selectedImages`; each click appends to the
 *     parent's growing list, already-added images are synthesized into the
 *     grid as locked tiles.
 */
export function ImagePickerBody({
  creativeId,
  onSelect,
  currentImageUrl,
  currentAttachmentId,
  selectedImages,
  disabled = false,
  disabledHint,
  acceptMime = 'image/*',
}: ImagePickerBodyProps) {
  const [images, setImages] = useState<GeneratedImage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploadWarning, setUploadWarning] = useState<string | null>(null)
  // Append mode: every selectedImages entry the parent has shown us this
  // session gets recorded here. The pool only grows — when the parent removes
  // an item from selectedImages (user clicked X on the strip), the tile stays
  // visible in the gallery; live check/lock state comes from selectedImages,
  // so the just-removed tile becomes un-checked + clickable to re-add.
  const [syntheticPool, setSyntheticPool] = useState<GeneratedImage[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { handleUpload, isUploading } = useImageUpload({ maxSize: 10 * 1024 * 1024 })

  useEffect(() => {
    if (!selectedImages?.length) return
    setSyntheticPool(prev => {
      const seen = new Set(prev.map(p => p.attachmentId || p.imageUrl))
      const additions: GeneratedImage[] = []
      for (const sel of selectedImages) {
        const key = sel.attachmentId || sel.imageUrl
        if (!key || seen.has(key)) continue
        additions.push({
          attachmentId: sel.attachmentId,
          imageUrl: sel.imageUrl,
          createdAt: '',
          mimeType: sel.mimeType,
          ratio: sel.ratio,
        })
        seen.add(key)
      }
      return additions.length ? [...prev, ...additions] : prev
    })
  }, [selectedImages])

  const loadImages = useCallback(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    apiCall(CreativeService.creativeControllerGetGeneratedImagesV1, null, creativeId)
      .then(data => {
        if (!cancelled) setImages(data.images || [])
      })
      .catch(err => {
        if (!cancelled) {
          setImages([])
          setError(err instanceof Error ? err.message : 'Failed to load images')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [creativeId])

  useEffect(() => loadImages(), [loadImages])

  const displayImages = useMemo(() => {
    // The gallery is a stable pool: API images first (canonical), then any
    // synthetic entries the parent has shown us this session, then the single-
    // mode "current image" if it's not already represented. Items are never
    // removed mid-session — removal from the parent just un-checks the tile.
    const seenIds = new Set(images.map(img => img.attachmentId).filter(Boolean))
    const seenUrls = new Set(images.map(img => img.imageUrl))
    const result: GeneratedImage[] = [...images]

    for (const synth of syntheticPool) {
      if (synth.attachmentId && seenIds.has(synth.attachmentId)) continue
      if (seenUrls.has(synth.imageUrl)) continue
      result.push(synth)
      if (synth.attachmentId) seenIds.add(synth.attachmentId)
      seenUrls.add(synth.imageUrl)
    }

    if (currentImageUrl) {
      const inGallery =
        (currentAttachmentId && seenIds.has(currentAttachmentId)) || seenUrls.has(currentImageUrl)
      if (!inGallery) {
        result.unshift({
          attachmentId: currentAttachmentId ?? '',
          imageUrl: currentImageUrl,
          createdAt: '',
        })
      }
    }

    return result
  }, [images, syntheticPool, currentImageUrl, currentAttachmentId])

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      // Carousel uploads must be JPEG; the platform rejects other mimes.
      if (acceptMime === 'image/jpeg' && file.type !== 'image/jpeg') {
        setUploadWarning('Instagram carousels require JPEG images.')
        if (fileInputRef.current) fileInputRef.current.value = ''
        return
      }
      setUploadWarning(null)

      try {
        const attachment = await handleUpload(file)
        onSelect({
          imageUrl: attachment.publicUrl,
          imageAttachmentId: attachment.id,
          source: 'uploaded',
          mimeType: attachment.mimeType,
          ratio: attachment.ratio,
        })
      } catch (err) {
        console.error('ImagePickerBody: upload failed:', err)
      }

      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [handleUpload, onSelect, acceptMime]
  )

  const isInSelected = (img: GeneratedImage) =>
    !!selectedImages?.some(
      sel =>
        (img.attachmentId && sel.attachmentId === img.attachmentId) || sel.imageUrl === img.imageUrl
    )

  const isSelected = (img: GeneratedImage) => {
    if (selectedImages?.length) return isInSelected(img)
    if (currentAttachmentId) return img.attachmentId === currentAttachmentId
    if (currentImageUrl) return img.imageUrl === currentImageUrl
    return false
  }

  // Append mode: already-added images are locked so the user can't double-add.
  // Single mode: nothing is locked — clicking the current image is a harmless no-op.
  const isLocked = (img: GeneratedImage) => {
    if (disabled) return true
    if (!selectedImages?.length) return false
    return isInSelected(img)
  }

  return (
    <div className="space-y-3">
      {disabled ? (
        <div className="flex w-full items-center justify-center gap-2 rounded-[10px] border-2 border-dashed border-line bg-bg-2 px-4 py-4 text-[13px] text-paper-faint">
          {disabledHint ?? 'Upload disabled'}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="group flex w-full items-center justify-center gap-2 rounded-[10px] border-2 border-dashed border-line bg-bg-2 px-4 py-4 text-[13px] font-medium text-paper-faint transition-colors hover:border-acid hover:bg-[color:var(--acid-tint)] hover:text-acid disabled:opacity-50"
        >
          {isUploading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload
                size={18}
                className="transition-transform duration-200 ease-out group-hover:-translate-y-0.5"
              />
              Upload from device
            </>
          )}
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptMime}
        onChange={handleFileChange}
        className="hidden"
      />
      {uploadWarning && (
        <p className="text-[12px] text-[color:var(--alert-error-text)]">{uploadWarning}</p>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-paper-faint" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center">
          <ImageIcon size={32} className="text-paper-faint" />
          <p className="text-[13px] text-paper-dim">{error}</p>
          <button
            type="button"
            onClick={loadImages}
            className="mt-1 text-[13px] font-medium text-acid hover:underline"
          >
            Try again
          </button>
        </div>
      ) : displayImages.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center">
          <ImageIcon size={32} className="text-paper-faint" />
          <p className="text-[13px] text-paper-dim">No images created yet.</p>
          <p className="text-[12px] text-paper-faint">
            Use the chat to create images, or upload your own.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
            Created images
          </p>
          <div className="grid grid-cols-3 gap-3">
            {displayImages.map(img => {
              const selected = isSelected(img)
              const locked = isLocked(img)
              return (
                <button
                  key={img.attachmentId || img.imageUrl}
                  type="button"
                  disabled={locked}
                  onClick={() =>
                    onSelect({
                      imageUrl: img.imageUrl,
                      imageAttachmentId: img.attachmentId,
                      source: 'generated',
                      mimeType: img.mimeType,
                      ratio: img.ratio,
                    })
                  }
                  className={`group relative overflow-hidden rounded-[8px] border-2 transition-all ${
                    selected
                      ? 'border-acid shadow-[0_0_0_3px_var(--acid-soft)]'
                      : 'border-line hover:border-line-bright'
                  } ${locked ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <img src={img.imageUrl} alt="" className="aspect-video w-full object-cover" />
                  {selected && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <div className="rounded-full bg-acid p-1.5">
                        <Check size={14} className="text-ink-on-acid" />
                      </div>
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
